import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Loader2, HeartHandshake, Search, Phone, Mail, Plus, RotateCcw, MapPin, User,
} from "lucide-react";
import { pretty, REL_STATUS_HEX, hexWithAlpha, GRAY } from "./crmConstants";

const StatusChip = ({ status, testid }) => {
  const hex = REL_STATUS_HEX[status] || GRAY;
  return (
    <span data-testid={testid} className="text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
      style={{ backgroundColor: hexWithAlpha(hex, 0.14), color: hex }}>{pretty(status)}</span>
  );
};

const EMPTY_NEW = {
  name: "", relationship_type: "senior_living_facility", contact_person: "", contact_role: "",
  phone: "", email: "", website: "", city: "", state_province: "", status: "new",
  territory_id: "", franchise_id: "", notes: "",
};

export default function CrmRelationships() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ q: "", relationship_type: "all", status: "all", territory_id: "all", franchise_id: "all" });
  const [open, setOpen] = useState(false);
  const [nu, setNu] = useState(EMPTY_NEW);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        q: f.q || undefined,
        relationship_type: f.relationship_type === "all" ? undefined : f.relationship_type,
        status: f.status === "all" ? undefined : f.status,
        territory_id: f.territory_id === "all" ? undefined : f.territory_id,
        franchise_id: f.franchise_id === "all" ? undefined : f.franchise_id,
      };
      const { data } = await api.get("/crm/relationships", { params });
      setData(data);
    } catch { toast.error("Could not load relationships."); }
    finally { setLoading(false); }
  }, [f]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!nu.name.trim()) { toast.error("Name is required."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/crm/relationships", nu);
      toast.success("Relationship created.");
      setOpen(false); setNu(EMPTY_NEW);
      navigate(`/crm/relationships/${data.relationship.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create."); }
    finally { setSaving(false); }
  };

  const types = data?.types || [];
  const statuses = data?.statuses || [];
  const counts = data?.counts || {};
  const names = data?.user_names || {};
  const territories = data?.territories || [];
  const franchises = data?.franchises || [];

  return (
    <div className="pb-12" data-testid="crm-relationships">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <HeartHandshake size={22} className="text-[#1C4358]" />
          <h1 className="text-2xl font-display font-bold text-[#1C4358]">Relationships</h1>
          {data && <span className="text-[13px] text-[#4B5563]">{data.items.length} partners / vendors</span>}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} className="border-[#D6E0E7] text-[#1C4358] gap-1"><RotateCcw size={14} /> Refresh</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="rel-new-btn" className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1"><Plus size={15} /> New Relationship</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="rel-create-dialog">
              <DialogHeader><DialogTitle className="font-display text-[#1C4358]">New Relationship</DialogTitle></DialogHeader>
              <div className="grid sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
                <div className="sm:col-span-2">
                  <label className="text-[11px] text-[#4B5563]">Name *</label>
                  <Input data-testid="rel-new-name" value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} placeholder="Sunrise Assisted Living" className="h-9 text-[14px]" />
                </div>
                <div>
                  <label className="text-[11px] text-[#4B5563]">Type</label>
                  <Select value={nu.relationship_type} onValueChange={(v) => setNu({ ...nu, relationship_type: v })}>
                    <SelectTrigger data-testid="rel-new-type" className="h-9 text-[13px]"><SelectValue /></SelectTrigger>
                    <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{pretty(t)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[11px] text-[#4B5563]">Status</label>
                  <Select value={nu.status} onValueChange={(v) => setNu({ ...nu, status: v })}>
                    <SelectTrigger data-testid="rel-new-status" className="h-9 text-[13px]"><SelectValue /></SelectTrigger>
                    <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{pretty(s)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><label className="text-[11px] text-[#4B5563]">Contact person</label><Input data-testid="rel-new-contact" value={nu.contact_person} onChange={(e) => setNu({ ...nu, contact_person: e.target.value })} className="h-9 text-[14px]" /></div>
                <div><label className="text-[11px] text-[#4B5563]">Contact role</label><Input value={nu.contact_role} onChange={(e) => setNu({ ...nu, contact_role: e.target.value })} className="h-9 text-[14px]" /></div>
                <div><label className="text-[11px] text-[#4B5563]">Phone</label><Input data-testid="rel-new-phone" value={nu.phone} onChange={(e) => setNu({ ...nu, phone: e.target.value })} className="h-9 text-[14px]" /></div>
                <div><label className="text-[11px] text-[#4B5563]">Email</label><Input value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} className="h-9 text-[14px]" /></div>
                <div><label className="text-[11px] text-[#4B5563]">City</label><Input value={nu.city} onChange={(e) => setNu({ ...nu, city: e.target.value })} className="h-9 text-[14px]" /></div>
                <div><label className="text-[11px] text-[#4B5563]">State/Province</label><Input value={nu.state_province} onChange={(e) => setNu({ ...nu, state_province: e.target.value })} className="h-9 text-[14px]" /></div>
                <div className="sm:col-span-2"><label className="text-[11px] text-[#4B5563]">Website</label><Input value={nu.website} onChange={(e) => setNu({ ...nu, website: e.target.value })} className="h-9 text-[14px]" /></div>
                <div className="sm:col-span-2"><label className="text-[11px] text-[#4B5563]">Notes</label><Textarea value={nu.notes} onChange={(e) => setNu({ ...nu, notes: e.target.value })} rows={2} className="text-[14px]" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="border-[#D6E0E7] text-[#1C4358]">Cancel</Button>
                <Button data-testid="rel-create-submit" onClick={create} disabled={saving} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1">{saving && <Loader2 size={14} className="animate-spin" />} Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Status counts */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {statuses.map((s) => {
          const hex = REL_STATUS_HEX[s] || GRAY;
          return (
            <button key={s} data-testid={`rel-count-${s}`} onClick={() => setF((p) => ({ ...p, status: p.status === s ? "all" : s }))}
              className="text-[12px] font-semibold px-2.5 py-1 rounded-full border transition-colors"
              style={{ borderColor: hexWithAlpha(hex, 0.3), color: hex, backgroundColor: f.status === s ? hexWithAlpha(hex, 0.12) : "white" }}>
              {pretty(s)} <span className="tabular-nums">{counts[s] || 0}</span>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative"><Search size={15} className="absolute left-2.5 top-2.5 text-[#4B5563]" /><Input data-testid="rel-search" value={f.q} onChange={(e) => setF((p) => ({ ...p, q: e.target.value }))} placeholder="Search name / contact / phone" className="pl-8 h-9 w-64 text-[14px] rounded-lg border-[#1C4358]/20" /></div>
        <Select value={f.relationship_type} onValueChange={(v) => setF((p) => ({ ...p, relationship_type: v }))}><SelectTrigger data-testid="rel-filter-type" className="w-[190px] h-9 text-[13px]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All types</SelectItem>{types.map((t) => <SelectItem key={t} value={t}>{pretty(t)}</SelectItem>)}</SelectContent></Select>
        <Select value={f.territory_id} onValueChange={(v) => setF((p) => ({ ...p, territory_id: v }))}><SelectTrigger data-testid="rel-filter-territory" className="w-[170px] h-9 text-[13px]"><SelectValue placeholder="All territories" /></SelectTrigger><SelectContent><SelectItem value="all">All territories</SelectItem>{territories.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select>
        <Select value={f.franchise_id} onValueChange={(v) => setF((p) => ({ ...p, franchise_id: v }))}><SelectTrigger data-testid="rel-filter-franchise" className="w-[170px] h-9 text-[13px]"><SelectValue placeholder="All franchises" /></SelectTrigger><SelectContent><SelectItem value="all">All franchises</SelectItem>{franchises.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select>
      </div>

      {loading && !data ? <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div> :
        (data.items || []).length === 0 ? <div className="text-center py-16 text-[#4B5563]"><HeartHandshake className="mx-auto mb-2 text-[#9AA5B1]" /> No relationships yet. Add your first partner or vendor.</div> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.items.map((r) => (
              <div key={r.id} data-testid={`rel-card-${r.id}`} onClick={() => navigate(`/crm/relationships/${r.id}`)}
                className="rounded-xl border border-[#E6EDF2] bg-white p-4 hover:border-[#1C4358]/40 transition-colors cursor-pointer">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-semibold text-[#1C4358] text-[15px] leading-tight">{r.name}</span>
                  <StatusChip status={r.status} testid={`rel-status-${r.id}`} />
                </div>
                <div className="text-[12px] text-[#4B5563] mt-1">{pretty(r.relationship_type)}</div>
                <div className="mt-2 space-y-1 text-[12px] text-[#4B5563]">
                  {r.contact_person && <div className="flex items-center gap-1"><User size={11} /> {r.contact_person}{r.contact_role ? ` · ${r.contact_role}` : ""}</div>}
                  {r.phone && <div className="flex items-center gap-1"><Phone size={11} /> {r.phone}</div>}
                  {r.email && <div className="flex items-center gap-1 truncate"><Mail size={11} /> {r.email}</div>}
                  {(r.city || r.state_province) && <div className="flex items-center gap-1"><MapPin size={11} /> {r.city} {r.state_province}</div>}
                </div>
                {names[r.managed_by_user_id] && <div className="text-[11px] text-[#9AA5B1] mt-2">Managed by {names[r.managed_by_user_id]}</div>}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
